import * as React from 'react';
import { Input, Modal, Button } from 'antd';
import PricesRow from './components/PricesRow';
import { calculateRecommendedPrice } from 'src/helpers/Prices/calculateRecommendedPrice';
import { calculatePrice } from 'src/helpers/Prices/calculatePrice';

export interface IPricesWithDiscountInputControlProps {
  name: string;
  disabled?: boolean;
  showPurchasePrice?: boolean; 
  prices: GQL.IPrice[];
  mode: 'recommendedPriceWithout' | 'priceWithout' | 'minimalPrice'
  availabilityMode?: boolean;
  stockMode?: boolean;
  currencies?: GQL.ICurrency[];
  onChange: Function;
  sku: string | undefined;
  ean: string | undefined;
}

interface IPricesWithDiscountInputControlState {
  visible: boolean;
  prices: GQL.IPrice[];
  currency: GQL.ICurrency | undefined;
}

export class PricesWithDiscountInputControl extends React.Component<IPricesWithDiscountInputControlProps, IPricesWithDiscountInputControlState> {

  constructor(props: IPricesWithDiscountInputControlProps) {
    super(props);
    this.state = {
      visible: false,
      prices: [...props.prices],
      currency: props.currencies ? props.currencies.find((c: GQL.ICurrency) => c.primary === true) : undefined,
    };
  }

  public componentDidUpdate(prevProps: Readonly<IPricesWithDiscountInputControlProps>): void {
    const { ean, sku } = this.props;
    const { ean: prevEan, sku: prevSku } = prevProps;
    if((ean !== prevEan) || (sku !== prevSku)) {
      this.setState({ prices: [...this.props.prices] });
    }
  }

  public render() {
    const { prices, mode, currencies, availabilityMode, stockMode, sku } = this.props;

    if (!currencies || !this.state.currency || this.state.currency === undefined) {
      return null;
    }

    const priceField = mode;

    if (this.state.currency === undefined) {
      return null;
    }

    const priceObject = prices.find((p: GQL.IPrice) => !!this.state.currency && p.currencyId === this.state.currency.id);
    const defaultCurrency = currencies.find((c: GQL.ICurrency) => c.primary === true);      
    const priceFields = currencies
      .sort((currency: GQL.ICurrency) => currency.primary ? -1 : 1)
      .map((currency: GQL.ICurrency, index: number) => {
        const innerPriceObject = prices.find((p: GQL.IPrice) => p.currencyId === currency.id);

        // doplneni informace o purchase pric pro USD
        return (
          <PricesRow
            sku={sku}
            showPurchasePrice={!!this.props.showPurchasePrice}
            key={index}
            name={this.props.name}
            prices={prices}
            defaultCurrency={defaultCurrency}
            currency={currency}
            priceObject={innerPriceObject || {} as GQL.IPrice}
            availabilityMode={availabilityMode}
            stockMode={stockMode}
            onChange={(e: any) => this.setState({ prices: e.target.value })}
            disabled={this.props.disabled}
          />
        );
      });

    return (
      <div className="price-input">
        <Input
          name={priceField}
          type={'number'}
          value={`${priceObject && priceObject[priceField] ? priceObject[priceField] : ''}`}
          step="0.01"
          onChange={this.onChangePrice}
          addonAfter={defaultCurrency?.value}
          disabled={this.props.disabled}
        />

        <Button
          onClick={e => {
            this.setState({
              visible: true,
            });
          }}
        >
          More
        </Button>
        <Modal
          title={'Advanced pricing'}
          destroyOnClose={true}
          visible={this.state.visible}
          width={1200}
          onCancel={e => {
            this.setState({
              visible: false
            });
          }}
          onOk={e => {
            if (!this.props.disabled) {
              this.onChangePrices(this.state.prices);
            }

            this.setState({
              visible: false
            });
          }}
          maskClosable={false}
        >
          <div className="additional-currencies">
            {priceFields}
          </div>
        </Modal>
      </div>
    );
  }

  onChangePrice = (e: any) => {
    const { name, value } = e.target;
    let priceFound = false;
    const newPrices = [...this.props.prices].map((price: GQL.IPrice) => {
      if (this.state.currency && (price.currencyId === this.state.currency.id)) {
        price[name] = parseFloat(value);
        priceFound = true;
      }
      return price;
    });

    if (!priceFound && this.state.currency) {
      newPrices.push({
        currencyId: this.state.currency.id,
        [name]: parseFloat(value),
      } as any);
    }

    this.updatePrice(newPrices);
  }

  private updatePrice(newPrices: GQL.IPrice[]) {
    if (this.props.disabled === true) {
      return;
    }

    const { mode } = this.props;

    // pokud zadávám novou cenu, přidá se k současným
    const mergedPrices = [...this.state.prices];
    newPrices.forEach((newPrice: GQL.IPrice) => {
      const existsPrice = mergedPrices.find((price: GQL.IPrice) => {
        return (
          !!this.state.currency
            && !!this.state.currency.id
            && !!price.currencyId
            && !!newPrice.currencyId
            && newPrice.currencyId === price.currencyId
        );
      });
      if (!existsPrice) {
        mergedPrices.push(newPrice);
      }
    });

    // přepočet cen oběma stranami, tzn. započítání slev
    const prices = [...mergedPrices].map((price: GQL.IPrice) => {
      if (!!this.state.currency && price.currencyId && this.state.currency.id) {
        // nová ceny v měně
        const newPrice = newPrices.find((p: GQL.IPrice) => p.currencyId === price.currencyId);
        if (newPrice) {
          // konečná cena
          if (mode !== 'recommendedPriceWithout') {
            newPrice.priceWithout = newPrice.priceWithout;
            newPrice.recommendedPriceWithout = calculateRecommendedPrice({...newPrice});
          }
          // bežná (doporučená) cena
          if (mode === 'recommendedPriceWithout') {
            newPrice.recommendedPriceWithout = newPrice.recommendedPriceWithout;
            newPrice.priceWithout = calculatePrice({...newPrice});
          }
          return newPrice;
        }
      }

      return price;
    });

    // odeslání změny cen
    this.onChangePrices(prices);
  }

  onChangePrices = (newPrices: GQL.IPrice[]) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: newPrices.map((price: GQL.IPrice) => {
          if (price.priceWithout === 0) {
            price.priceWithout = null;
          }
          if (price.recommendedPriceWithout === 0) {
            price.recommendedPriceWithout = null;
          }

          return price;
        }),
      }
    });
  }
}

export default PricesWithDiscountInputControl;


// WEBPACK FOOTER //
// ./src/components/Others/PricesWithDiscountInput/PricesWithDiscountInput.tsx